import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarCheck, FileText, Send, ClipboardCheck, Loader2, HeartHandshake, CheckCircle2, Download } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";

export default function AdminNdisOps() {
  const [participants, setParticipants] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [attendHours, setAttendHours] = useState({});
  const [report, setReport] = useState({ participant_id: "", extra_focus_hours: 0, notes: "", email_coordinator: false });
  const [reportResult, setReportResult] = useState(null);
  const [form, setForm] = useState({ participant_id: "", title: "", scheduled_at: "", duration_min: 60, group: false });

  const load = useCallback(() => {
    api.get("/admin/ndis/participants").then((r) => setParticipants(r.data)).catch(() => {});
    api.get("/admin/ndis/sessions").then((r) => setSessions(r.data)).catch(() => {});
    api.get("/admin/ndis/invoices").then((r) => setInvoices(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try {
      const r = await fn();
      toast.success(ok || r.data?.message || "Done");
      load();
      return r.data;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const schedule = () => act(async () => {
    await api.post("/admin/ndis/sessions", { ...form, duration_min: parseInt(form.duration_min, 10) || 60 });
    setForm({ participant_id: "", title: "", scheduled_at: "", duration_min: 60, group: false });
  });

  const markAttendance = (sid, attended) => act(() => api.post(`/admin/ndis/sessions/${sid}/attendance`, { attended, hours: parseFloat(attendHours[sid]) || 1 }));

  const generateReport = () => act(async () => {
    const r = await api.post(`/admin/ndis/participants/${report.participant_id}/report`, { ...report, extra_focus_hours: parseFloat(report.extra_focus_hours) || 0 });
    setReportResult(r.data);
  }, "Progress report generated");

  return (
    <section data-testid="admin-ndisops-section" className="flex flex-col gap-4">
      <div className="border border-white/10 bg-black/40">
        <div className="border-b border-white/10 px-6 py-4">
          <h2 className="font-sans font-semibold uppercase tracking-widest text-base text-zinc-100 flex items-center gap-2">
            <HeartHandshake className="w-4 h-4 text-nc-orange" /> NDIS Ops — Sessions, Invoicing & Plan Reviews
          </h2>
          <p className="font-mono text-[10px] text-zinc-500 mt-2 leading-relaxed">
            The automated loop: schedule a session → mark attendance → the invoice draft maps itself to the participant's track line item → approve & send to the plan manager. Progress reports compile participant metrics into a plan-review PDF.
          </p>
        </div>

        <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sessions */}
          <div className="flex flex-col gap-3">
            <p className="font-mono text-[9px] tracking-[0.25em] text-nc-blue uppercase flex items-center gap-2"><CalendarCheck className="w-3.5 h-3.5" /> Mentor Sessions</p>
            <div className="border border-white/10 bg-[#050505]/50 p-4 flex flex-col gap-2.5">
              <div>
                <label className={labelCls}>Participant</label>
                <select data-testid="ndis-session-participant" className={inputCls} value={form.participant_id} onChange={(e) => setForm((f) => ({ ...f, participant_id: e.target.value }))}>
                  <option value="">Select participant…</option>
                  {participants.map((p) => <option key={p.id} value={p.id}>{`${p.participant_name} (${p.track})`}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Session title</label>
                <input data-testid="ndis-session-title" className={inputCls} placeholder="e.g. Week 2 studio sprint" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} maxLength={120} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelCls}>When</label>
                  <input data-testid="ndis-session-when" type="datetime-local" className={inputCls} value={form.scheduled_at} onChange={(e) => setForm((f) => ({ ...f, scheduled_at: e.target.value }))} />
                </div>
                <div>
                  <label className={labelCls}>Duration (min)</label>
                  <input data-testid="ndis-session-duration" type="number" min={15} max={480} className={inputCls} value={form.duration_min} onChange={(e) => setForm((f) => ({ ...f, duration_min: e.target.value }))} />
                </div>
              </div>
              <label className="flex items-center gap-2 font-mono text-[10px] text-zinc-400 cursor-pointer">
                <input type="checkbox" data-testid="ndis-session-group" checked={form.group} onChange={(e) => setForm((f) => ({ ...f, group: e.target.checked }))} className="w-3.5 h-3.5 accent-[#C74A00]" /> Group studio sprint
              </label>
              <button data-testid="ndis-session-create" disabled={busy || !form.participant_id || form.title.length < 3 || !form.scheduled_at} onClick={schedule} className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-4 py-2.5 transition-colors">
                Schedule Session
              </button>
            </div>
            <div className="flex flex-col gap-2 max-h-72 overflow-y-auto">
              {sessions.length === 0 && <p className="font-mono text-[10px] text-zinc-600">No sessions scheduled yet.</p>}
              {sessions.map((s, i) => (
                <div key={s.id} data-testid={`ndis-session-${i + 1}`} className="border border-white/10 bg-black/40 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-sans font-semibold text-xs text-zinc-100 truncate">{s.title}</span>
                    <span className="font-mono text-[9px] text-zinc-500 shrink-0">{String(s.scheduled_at).slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <p className="font-mono text-[9px] text-zinc-500 mt-1">{s.participant_name} · {s.duration_min}min{s.group ? " · group" : ""}</p>
                  {s.attendance ? (
                    <p data-testid={`ndis-session-marked-${i + 1}`} className={`font-mono text-[9px] tracking-[0.2em] uppercase mt-2 ${s.attendance.attended ? "text-nc-green" : "text-zinc-500"}`}>
                      {s.attendance.attended ? `Attended — ${s.attendance.hours}h → invoice drafted` : "Marked absent"}
                    </p>
                  ) : (
                    <div className="flex items-center gap-2 mt-2">
                      <input type="number" min={0.5} max={12} step={0.5} data-testid={`ndis-session-hours-${i + 1}`} className={`${inputCls} w-16 text-center`} value={attendHours[s.id] || 1} onChange={(e) => setAttendHours((h) => ({ ...h, [s.id]: e.target.value }))} aria-label="Hours attended" />
                      <button data-testid={`ndis-session-attend-${i + 1}`} disabled={busy} onClick={() => markAttendance(s.id, true)} className="font-mono text-[9px] uppercase px-2.5 py-1.5 bg-nc-green/90 hover:bg-nc-green text-black transition-colors">Attended</button>
                      <button data-testid={`ndis-session-absent-${i + 1}`} disabled={busy} onClick={() => markAttendance(s.id, false)} className="font-mono text-[9px] uppercase px-2.5 py-1.5 border border-white/15 text-zinc-400 hover:border-white/40 transition-colors">Absent</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Invoices */}
          <div className="flex flex-col gap-3">
            <p className="font-mono text-[9px] tracking-[0.25em] text-nc-orange uppercase flex items-center gap-2"><FileText className="w-3.5 h-3.5" /> Statement Register</p>
            <div className="flex flex-col gap-2 max-h-[430px] overflow-y-auto">
              {invoices.length === 0 && <p className="font-mono text-[10px] text-zinc-600">No statements yet — they appear when attendance is marked or an agreement is signed.</p>}
              {invoices.map((inv, i) => (
                <div key={inv.id} data-testid={`ndis-invoice-${i + 1}`} className="border border-white/10 bg-black/40 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-sans font-semibold text-xs text-zinc-100 truncate">{inv.participant_name}</span>
                    <span className={`font-mono text-[9px] tracking-[0.2em] uppercase border px-2 py-0.5 ${inv.status === "sent" ? "text-nc-green border-nc-green/40" : "text-nc-orange border-nc-orange/40"}`}>{inv.status}</span>
                  </div>
                  <p className="font-mono text-[9px] text-zinc-500 mt-1 truncate">{inv.line_items.map((li) => li.code).join(" + ")} · {inv.line_items.reduce((a, li) => a + (li.amount || 0), 0) > 0 ? `$${inv.line_items.reduce((a, li) => a + (li.amount || 0), 0).toFixed(2)}` : "hourly"} → {inv.plan_manager_email}</p>
                  {inv.status === "draft" && (
                    <button data-testid={`ndis-invoice-approve-${i + 1}`} disabled={busy} onClick={() => act(() => api.post(`/admin/ndis/invoices/${inv.id}/approve`))} className="mt-2 font-mono text-[9px] uppercase px-3 py-2 bg-nc-orange hover:bg-nc-orange/80 text-black transition-colors flex items-center gap-1.5 disabled:opacity-50">
                      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Approve & Send to Plan Manager
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Progress reports */}
          <div className="flex flex-col gap-3">
            <p className="font-mono text-[9px] tracking-[0.25em] text-nc-green uppercase flex items-center gap-2"><ClipboardCheck className="w-3.5 h-3.5" /> Plan Review Reports</p>
            <div className="border border-white/10 bg-[#050505]/50 p-4 flex flex-col gap-2.5">
              <div>
                <label className={labelCls}>Participant</label>
                <select data-testid="ndis-report-participant" className={inputCls} value={report.participant_id} onChange={(e) => setReport((r) => ({ ...r, participant_id: e.target.value }))}>
                  <option value="">Select participant…</option>
                  {participants.map((p) => <option key={p.id} value={p.id}>{p.participant_name}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Extra focus hours (self-paced work)</label>
                <input data-testid="ndis-report-hours" type="number" min={0} step={0.5} className={inputCls} value={report.extra_focus_hours} onChange={(e) => setReport((r) => ({ ...r, extra_focus_hours: e.target.value }))} />
              </div>
              <div>
                <label className={labelCls}>Mentor notes (optional)</label>
                <textarea data-testid="ndis-report-notes" rows={2} className={`${inputCls} resize-y`} value={report.notes} onChange={(e) => setReport((r) => ({ ...r, notes: e.target.value }))} maxLength={2000} />
              </div>
              <label className="flex items-center gap-2 font-mono text-[10px] text-zinc-400 cursor-pointer">
                <input type="checkbox" data-testid="ndis-report-email" checked={report.email_coordinator} onChange={(e) => setReport((r) => ({ ...r, email_coordinator: e.target.checked }))} className="w-3.5 h-3.5 accent-[#2E6BFF]" /> Email report to coordinator / oversight address
              </label>
              <button data-testid="ndis-report-generate" disabled={busy || !report.participant_id} onClick={generateReport} className="bg-nc-green/90 hover:bg-nc-green disabled:opacity-40 text-black font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-4 py-2.5 transition-colors">
                Generate Plan Review Report
              </button>
            </div>
            {reportResult && (
              <div data-testid="ndis-report-result" className="border border-nc-green/40 bg-nc-green/5 p-4">
                <p className="font-mono text-[10px] text-nc-green uppercase tracking-[0.2em] flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5" /> Report ready{reportResult.emailed ? " + emailed" : ""}</p>
                <p className="font-mono text-[10px] text-zinc-300 mt-2 leading-relaxed">
                  {reportResult.metrics.models_completed} models · {reportResult.metrics.focus_hours}h focus · {reportResult.metrics.packs_listed} packs listed · {reportResult.metrics.sessions_attended} sessions
                </p>
                <a data-testid="ndis-report-download" href={reportResult.pdf_url} className="mt-2 inline-flex items-center gap-2 font-mono text-[10px] text-nc-blue hover:underline"><Download className="w-3.5 h-3.5" /> Download PDF</a>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
